import { useEffect, useState } from 'react';
import { requests } from '../../services/external/APIs';
import { renderErrorMessage } from '../../utils/renderErrorMessage';
import ErrorMessage from './ErrorMessage';
import Loading from './Loading';
import Pager from './Pager';
import PageSizeSelector from './PageSizeSelector';
import SearchInput from './SearchInput';

interface TableComponentWithDataProps {
    data: any[];
    requestReload?: () => void;
    onSortingChange?: (columnName: string, sortOrder: 'asc' | 'desc') => void;
    sortedBy?: string | null;
    ascending?: boolean | null;
}

export default function withFilteredQueryRequest(
    url: string,
    Component: React.ComponentType<TableComponentWithDataProps>
) {
    return function () {
        const [isLoading, setIsLoading] = useState<boolean>(false);
        const [data, setData] = useState<any>([]);
        const [searchTerm, setSearchTerm] = useState<string>('');
        const [pageIndex, setPageIndex] = useState<number>(0);
        const [pageSize, setPageSize] = useState<number>(20);
        const [filteredRecords, setFilteredRecords] = useState<number>(0);
        const [totalRecords, setTotalRecords] = useState<number>(0);
        const [hasErrors, setHasErrors] = useState<boolean>(false);
        const [error, setError] = useState({
            title: '',
            message: '',
        });
        const [dataReloadedCount, setDataReloadedCount] = useState(0);
        const [sortingColumn, setSortingColumn] = useState<string>('');
        const [ascending, setAscending] = useState<boolean>(true);

        function buildRequestUrl(url: string) {
            let requestUrl = `${url}`;

            if (requestUrl.indexOf('=') > -1) {
                requestUrl = `${requestUrl}&searchTerm=${searchTerm}&offset=${
                    pageIndex * pageSize
                }&top=${pageSize}&orderBy=${sortingColumn}&ascending=${ascending}`;

                return requestUrl;
            }

            requestUrl = `${requestUrl}?searchTerm=${searchTerm}&offset=${
                pageIndex * pageSize
            }&top=${pageSize}&orderBy=${sortingColumn}&ascending=${ascending}`;

            return requestUrl;
        }

        const handleSortingChange = (
            columnName: string,
            sortOrder: 'asc' | 'desc'
        ) => {
            setAscending(sortOrder === 'asc');
            setSortingColumn(columnName);
        };

        const requestReloadHandler = () => {
            setDataReloadedCount(dataReloadedCount + 1);
        };

        useEffect(() => {
            setIsLoading(true);
            setHasErrors(false);
            setError({
                title: '',
                message: '',
            });
            const requestUrl: string = buildRequestUrl(url);

            requests
                .get(requestUrl)
                .then((response) => {
                    setData(response.data);
                    setFilteredRecords(response.filteredRecordsCount);
                    setTotalRecords(response.totalRecordsCount);
                    setIsLoading(false);
                })
                .catch((error) => {
                    const errormessage = renderErrorMessage(
                        error.response.status,
                        error.message
                    );
                    setHasErrors(true);
                    setIsLoading(false);
                    setError(errormessage);
                });
        }, [
            searchTerm,
            pageIndex,
            pageSize,
            dataReloadedCount,
            sortingColumn,
            ascending,
        ]);

        function handleSearchTermChange(e: any) {
            setPageIndex(0);
            setSearchTerm(e.target.value);
        }

        function handlePageIndexChange(page: number) {
            setPageIndex(page);
        }

        function handlePageSizeChange(e: any) {
            setPageIndex(0);
            setPageSize(parseInt(e.target.value));
        }

        return (
            <>
                <div className='row m-0 mb-3'>
                    <div className='col-sm-6 col'>
                        <PageSizeSelector
                            initialPageSize={pageSize}
                            handleChange={handlePageSizeChange}
                        />
                    </div>
                    <div className='col-sm-6 col '>
                        <div className='d-flex justify-content-sm-end'>
                            <div className='search-box ms-2 gridjs-search'>
                                <SearchInput
                                    value={searchTerm}
                                    handleChange={handleSearchTermChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {isLoading ? (
                    <Loading hasSkeleton={true} skeletonType='table' />
                ) : hasErrors ? (
                    <ErrorMessage
                        errorDescription={error.message}
                        title={error.title}
                    />
                ) : (
                    <Component
                        sortedBy={sortingColumn}
                        ascending={ascending}
                        onSortingChange={handleSortingChange}
                        data={data}
                        requestReload={requestReloadHandler}
                    />
                )}
                <Pager
                    visible={!isLoading && !hasErrors}
                    handleChange={handlePageIndexChange}
                    currentPageIndex={pageIndex}
                    pageSize={pageSize}
                    filteredRecords={filteredRecords}
                    totalRecords={totalRecords}
                />
            </>
        );
    };
}
